import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";

/**
 * RowActionsMenu — Standardized ⋮ dropdown for table row actions.
 * Menu is portaled + fixed so it is not clipped by overflow:hidden table wrappers.
 *
 * Usage:
 *   <RowActionsMenu
 *     testid={`actions-${row.id}`}
 *     items={[
 *       { label: "Edit",   icon: Pencil, onClick: () => edit(row) },
 *       { label: "Delete", icon: Trash2, onClick: () => del(row), danger: true },
 *       { label: "Convert", icon: UserCheck, onClick: () => convert(row), hidden: row.type !== "Intern" },
 *     ]}
 *   />
 */
export default function RowActionsMenu({ items = [], testid = "row-actions", align = "right" }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const visible = items.filter((i) => !i.hidden && i);
  const placeMenu = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const menuWidth = 176; // w-44
    const gap = 4;
    let left = align === "right" ? rect.right - menuWidth : rect.left;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    let top = rect.bottom + gap;
    const estimatedHeight = Math.min(320, 8 + visible.length * 36);
    if (top + estimatedHeight > window.innerHeight - 8) {
      top = Math.max(8, rect.top - estimatedHeight - gap);
    }
    setCoords({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    placeMenu();
  }, [open, align, visible.length]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onReposition = () => placeMenu();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [open, align, visible.length]);

  if (visible.length === 0) return null;

  return (
    <>
      <div className="relative inline-block">
        <button
          ref={btnRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          data-testid={testid}
          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
          title="Actions"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <MoreVertical className="h-4 w-4" />
        </button>
      </div>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            data-testid={`${testid}-menu`}
            role="menu"
            style={{ position: "fixed", top: coords.top, left: coords.left }}
            className="z-[9999] w-44 bg-white border border-slate-200 rounded shadow-xl py-1"
          >
            {visible.map((it, idx) => (
              <button
                key={idx}
                type="button"
                role="menuitem"
                data-testid={it.testid || `${testid}-item-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  it.onClick?.();
                }}
                disabled={it.disabled}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                  it.danger ? "text-rose-600 hover:bg-rose-50" : "text-slate-700 hover:bg-slate-50"
                }`}
              >
                {it.icon && <it.icon className="h-3.5 w-3.5" />}
                {it.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
